import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { AuthImagePanel } from '../../components/AuthImagePanel/AuthImagePanel';

/**
 * Admin sign-in page — split layout: product images left, login form right.
 */
export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const { signIn, loading, error, clearError, isOwner, user, cooldownUntil, initialize } = useAuthStore();
  const [submitting, setSubmitting] = useState(false);

  // Initialize auth state (resolves loading)
  useEffect(() => {
    initialize();
  }, [initialize]);

  // If already signed in as owner, redirect
  useEffect(() => {
    if (user && isOwner) {
      navigate('/admin', { replace: true });
    }
  }, [user, isOwner, navigate]);

  // Cooldown timer display
  const [cooldownText, setCooldownText] = useState('');
  useEffect(() => {
    if (!cooldownUntil) {
      setCooldownText('');
      return;
    }
    const tick = () => {
      const remaining = cooldownUntil - Date.now();
      if (remaining <= 0) {
        setCooldownText('');
        return;
      }
      setCooldownText(`Try again in ${Math.ceil(remaining / 1000)}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setSubmitting(true);
    const res = await signIn(email, password);
    setSubmitting(false);
    if (res.success) {
      navigate('/admin', { replace: true });
    }
  };

  const isCoolingDown = cooldownUntil && Date.now() < cooldownUntil;

  return (
    <div className="min-h-screen flex bg-ui-background">
      {/* Left — image panel */}
      <AuthImagePanel />

      {/* Right — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          {/* Logo area */}
          <div className="mb-8">
            <Link to="/" className="text-3xl font-extrabold text-brand-dark italic tracking-tight">
              smothers
            </Link>
            <h1 className="text-2xl font-bold text-brand-dark mt-4">Welcome back</h1>
            <p className="text-sm text-brand-gray mt-1">Sign in to the admin panel</p>
          </div>

          {/* Error */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3 p-3 mb-5 bg-red-50 border border-error/20 rounded-xl"
              role="alert"
              aria-live="assertive"
            >
              <AlertCircle size={18} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{error}</p>
            </motion.div>
          )}

          {/* Cooldown */}
          {isCoolingDown && cooldownText && (
            <div className="flex items-center gap-2 p-3 mb-5 bg-warning/10 border border-warning/20 rounded-xl">
              <Loader2 size={16} className="text-warning animate-spin" />
              <p className="text-sm text-warning font-medium">{cooldownText}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-brand-dark mb-1.5">
                Email address
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ui-muted" />
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@smothers.com"
                  className="w-full pl-11 pr-4 py-3 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red transition-all placeholder:text-ui-muted"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-brand-dark mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ui-muted" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-11 pr-11 py-3 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red transition-all placeholder:text-ui-muted"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ui-muted hover:text-brand-dark transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Forgot password link */}
            <div className="flex justify-end">
              <Link
                to="/admin/forgot-password"
                className="text-sm font-medium text-brand-red hover:text-red-700 transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || isCoolingDown}
              className={clsx(
                'w-full flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl transition-all',
                submitting || isCoolingDown
                  ? 'bg-brand-red/60 text-brand-white cursor-not-allowed'
                  : 'bg-brand-red hover:bg-red-700 text-brand-white shadow-lg hover:shadow-xl'
              )}
            >
              {submitting && <Loader2 size={18} className="animate-spin" />}
              {submitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-brand-gray">
            <Link to="/" className="text-brand-red hover:text-red-700 font-medium">
              &larr; Back to website
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
